import React, { useCallback, useMemo } from 'react';
import { CompositeDataSetResumeAddressParserRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';

const ResumeAddressParserConfigStep = z.enum(['config', 'complete']);

type ResumeAddressParserConfigStep = z.infer<
  typeof ResumeAddressParserConfigStep
>;

export const DataSetCompositeRecipeResumeAddressParserConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetResumeAddressParserRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
}) => {
  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<ResumeAddressParserConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'config', display: 'Config' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<ResumeAddressParserConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <WorkflowDataSetSelector
              title={'Merge Dataset'}
              dataSetId={config.mergeDataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  mergeDataSetId: id ? id : '',
                });
              }}
              listDataSetsPattern={listDataSetsPattern}
              filterFn={isMergeAtsIntegrationDataSet}
            />
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [config, dataWizardPayload.selectedItem, listDataSetsPattern, setConfig]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
